import { employeeService } from "@/api/employees";
import { leaveService } from "@/api/leaves";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { StatusBadge } from "@/components/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { canCancelLeave, hasLeaveEndDatePassed } from "@/utils/leaveCancellation";
import { useToast } from "@/hooks/use-toast";
import { LeaveApplication, LeaveStatus } from "@/types";
import { formatLeaveType, leaveTypeOptions } from "@/utils/leaveType";
import { cn } from "@/utils/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Plus, X } from "lucide-react";
import { useState } from "react";

export default function Employee() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [leaveToCancel, setLeaveToCancel] = useState<LeaveApplication | null>(
    null,
  );
  const [formData, setFormData] = useState({
    startDate: "",
    endDate: "",
    reason: "",
    leaveType: "",
    totalDays: 0,
    managerIds: [] as string[],
  });

  const queryClient = useQueryClient();

  const { data: myLeaves = [], isLoading } = useQuery({
    queryKey: ["my-leaves", user?.id],
    queryFn: () => leaveService.getMyLeaves(user?.id || ""),
    enabled: !!user?.id,
  });

  const { data: managers = [], isLoading: isLoadingManagers } = useQuery({
    queryKey: ["managers-list"],
    queryFn: () => employeeService.getByRoles(["MANAGER"]),
    enabled: showForm,
    staleTime: Infinity,
  });

  const createLeaveMutation = useMutation({
    mutationFn: leaveService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-leaves"] });
      toast({
        title: "Leave application submitted",
        description: "Your leave request has been sent for approval",
      });
      setShowForm(false);
      setFormData({
        startDate: "",
        endDate: "",
        reason: "",
        leaveType: "",
        totalDays: 0,
        managerIds: [],
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const cancelLeaveMutation = useMutation({
    mutationFn: (leave: LeaveApplication) => leaveService.cancel(leave.id),
    onMutate: async (leave) => {
      const queryKey = ["my-leaves", user?.id];
      await queryClient.cancelQueries({ queryKey });
      await queryClient.cancelQueries({ queryKey: ["dashboard-summary"] });

      const previousLeaves =
        queryClient.getQueryData<LeaveApplication[]>(queryKey) || [];

      queryClient.setQueryData<LeaveApplication[]>(queryKey, (current) =>
        (current || previousLeaves).map((item) =>
          item.id === leave.id
            ? {
                ...item,
                status: LeaveStatus.CANCELLED,
                cancelledBy: user?.id ?? item.cancelledBy ?? null,
              }
            : item,
        ),
      );

      return { previousLeaves, queryKey };
    },
    onError: (error, _leave, context) => {
      if (context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousLeaves);
      }
      toast({
        title: "Error",
        description: getErrorMessage(error, "Failed to cancel leave"),
        variant: "destructive",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-leaves", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-summary"] });
      toast({
        title: "Leave cancelled",
        description: "Your leave request has been cancelled successfully",
      });
      setLeaveToCancel(null);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["my-leaves", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-summary"] });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Manager validation
    if (formData.managerIds.length === 0) {
      toast({
        title: "Validation Error",
        description: "Please select at least one manager",
        variant: "destructive",
      });
      return;
    }

    if (!formData.leaveType) {
      toast({
        title: "Validation Error",
        description: "Please select a leave type",
        variant: "destructive",
      });
      return;
    }

    // Calculate total days
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);

    if (end < start) {
      toast({
        title: "Validation Error",
        description: "End date must be greater than or equal to start date",
        variant: "destructive",
      });
      return;
    }

    const diffTime = Math.abs(end.getTime() - start.getTime());
    const totalDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end dates

    const employeeId = user?.id || "";

    createLeaveMutation.mutate({
      ...formData,
      startDate: new Date(formData.startDate).toISOString(),
      endDate: new Date(formData.endDate).toISOString(),
      totalDays,
      employeeId,
      managerIds: formData.managerIds,
      status: "PENDING",
    } as unknown as LeaveApplication);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-foreground">My Leaves</h2>
            <p className="text-muted-foreground mt-1">
              View your leave history and apply for new leave
            </p>
          </div>
          <Button onClick={() => setShowForm(!showForm)}>
            <Plus className="h-4 w-4 mr-2" />
            Apply for Leave
          </Button>
        </div>

        {showForm && (
          <Card className="shadow-soft">
            <CardHeader>
              <CardTitle>New Leave Application</CardTitle>
              <CardDescription>
                Fill in the details for your leave request
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="startDate">Start Date</Label>
                    <Input
                      id="startDate"
                      type="date"
                      value={formData.startDate}
                      onChange={(e) =>
                        setFormData({ ...formData, startDate: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="endDate">End Date</Label>
                    <Input
                      id="endDate"
                      type="date"
                      value={formData.endDate}
                      min={formData.startDate}
                      onChange={(e) =>
                        setFormData({ ...formData, endDate: e.target.value })
                      }
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>
                    Managers <span className="text-destructive">*</span>
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        className="w-full justify-between h-auto min-h-10 py-2"
                      >
                        {formData.managerIds.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {formData.managerIds.map((id) => {
                              const manager = managers.find((m) => m.id === id);
                              return (
                                <Badge
                                  key={id}
                                  variant="secondary"
                                  className="mr-1"
                                >
                                  {manager?.name || id}
                                  <div
                                    className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 cursor-pointer"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setFormData({
                                        ...formData,
                                        managerIds: formData.managerIds.filter(
                                          (managerId) => managerId !== id,
                                        ),
                                      });
                                    }}
                                  >
                                    <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                                  </div>
                                </Badge>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">
                            Select managers...
                          </span>
                        )}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[400px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search managers..." />
                        <CommandList>
                          {isLoadingManagers ? (
                            <div className="py-6 text-center text-sm text-muted-foreground">
                              Loading managers...
                            </div>
                          ) : (
                            <>
                              <CommandEmpty>No manager found.</CommandEmpty>
                              <CommandGroup>
                                {managers.map((manager) => (
                                  <CommandItem
                                    key={manager.id}
                                    value={manager.name}
                                    className="group"
                                    onSelect={() => {
                                      setFormData((prev) => {
                                        const exists = prev.managerIds.includes(
                                          manager.id,
                                        );
                                        return {
                                          ...prev,
                                          managerIds: exists
                                            ? prev.managerIds.filter(
                                                (id) => id !== manager.id,
                                              )
                                            : [...prev.managerIds, manager.id],
                                        };
                                      });
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        formData.managerIds.includes(manager.id)
                                          ? "opacity-100"
                                          : "opacity-0",
                                      )}
                                    />
                                    <div className="flex flex-col">
                                      <span>{manager.name}</span>
                                      <span className="text-xs text-muted-foreground group-data-[selected=true]:text-accent-foreground">
                                        {manager.email}
                                      </span>
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </>
                          )}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="leaveType">
                      Leave Type <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={formData.leaveType}
                      onValueChange={(value) =>
                        setFormData({ ...formData, leaveType: value })
                      }
                      required
                    >
                      <SelectTrigger id="leaveType">
                        <SelectValue placeholder="Select leave type" />
                      </SelectTrigger>
                      <SelectContent>
                        {leaveTypeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reason">Reason</Label>
                  <Textarea
                    id="reason"
                    placeholder="Enter reason for leave..."
                    value={formData.reason}
                    onChange={(e) =>
                      setFormData({ ...formData, reason: e.target.value })
                    }
                    required
                    rows={4}
                  />
                </div>

                <div className="flex gap-2 justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowForm(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit">Submit Application</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        <Card className="shadow-soft">
          <CardHeader>
            <CardTitle>Leave History</CardTitle>
            <CardDescription>
              Your past and current leave applications
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="text-center text-muted-foreground py-8">
                Loading leaves...
              </p>
            ) : myLeaves.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No leave applications found
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Applied Date (DD/MM/YYYY)</TableHead>
                      <TableHead>Leave Period (DD/MM/YYYY)</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Leave Type</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {myLeaves.map((leave) => (
                      <TableRow key={leave.id}>
                        <TableCell>
                          {(() => {
                            const date = new Date(
                              leave.createdAt || leave.appliedDate || "",
                            );
                            return `${date.toLocaleDateString()} (${date.toLocaleDateString('en-US', { weekday: 'long' })})`;
                          })()}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const startDate = new Date(leave.startDate);
                            const endDate = new Date(leave.endDate);
                            return `${startDate.toLocaleDateString()} (${startDate.toLocaleDateString('en-US', { weekday: 'long' })}) - ${endDate.toLocaleDateString()} (${endDate.toLocaleDateString('en-US', { weekday: 'long' })})`;
                          })()}
                        </TableCell>
                        <TableCell>
                          {leave.totalDays || "Multiple"} days
                        </TableCell>
                        <TableCell>{leave.leaveType}</TableCell>
                        <TableCell className="max-w-xs truncate">
                          {leave.reason}
                        </TableCell>
                        <TableCell>
                          {leave.status === "REJECTED" &&
                          (leave.rejectionReason || leave.managerRemark) ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="cursor-help w-fit">
                                    <StatusBadge status={leave.status} />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="max-w-sm break-words">
                                    {leave.rejectionReason ||
                                      leave.managerRemark}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <StatusBadge status={leave.status} />
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {(leave.status === LeaveStatus.PENDING ||
                            leave.status === LeaveStatus.APPROVED) && (
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              disabled={!canCancelLeave(leave)}
                              title={
                                leave.status === LeaveStatus.APPROVED &&
                                hasLeaveEndDatePassed(leave.endDate)
                                  ? "Cannot cancel leave after leave date has passed"
                                  : undefined
                              }
                              onClick={() => {
                                if (!canCancelLeave(leave)) {
                                  toast({
                                    title: "Cannot cancel leave",
                                    description:
                                      "Cannot cancel leave after leave date has passed",
                                    variant: "destructive",
                                  });
                                  return;
                                }
                                setLeaveToCancel(leave);
                              }}
                            >
                              Cancel
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <AlertDialog
          open={!!leaveToCancel}
          onOpenChange={(open) => {
            if (!open) {
              setLeaveToCancel(null);
            }
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel leave request?</AlertDialogTitle>
              <AlertDialogDescription>
                {leaveToCancel ? (
                  <>
                    This will cancel your{" "}
                    {formatLeaveType(leaveToCancel.leaveType)} leave from{" "}
                    {new Date(leaveToCancel.startDate).toLocaleDateString()} to{" "}
                    {new Date(leaveToCancel.endDate).toLocaleDateString()}.
                    {leaveToCancel.status === LeaveStatus.APPROVED
                      ? " Your leave balance will be restored."
                      : " This action cannot be undone."}
                  </>
                ) : null}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={cancelLeaveMutation.isPending}>
                Keep request
              </AlertDialogCancel>
              <AlertDialogAction
                disabled={cancelLeaveMutation.isPending}
                onClick={(event) => {
                  event.preventDefault();
                  if (leaveToCancel) {
                    if (!canCancelLeave(leaveToCancel)) {
                      toast({
                        title: "Cannot cancel leave",
                        description:
                          "Cannot cancel leave after leave date has passed",
                        variant: "destructive",
                      });
                      setLeaveToCancel(null);
                      return;
                    }
                    cancelLeaveMutation.mutate(leaveToCancel);
                  }
                }}
              >
                {cancelLeaveMutation.isPending ? "Cancelling..." : "Cancel leave"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
